"use client";

import React, { useEffect, useState, useMemo } from "react";
import { RoundResult } from "@/lib/types/common/GameState";
import { useMainMap } from "@/app/providers/MainMapContextProvider";
import Image from "next/image";
import { convertDistanceToYalms } from "@/lib/utils/coordinateConversion";
import { useGame } from "@/app/providers/GameContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import TooltipWithIconFFXIV from "@/app/components/TooltipWithIconFFXIV";
import { animate, AnimatePresence, motion } from "framer-motion";
import { PRECISION_MIN_DISTANCE } from "@/lib/types/common/ScoringConstants";
import { FaFireFlameCurved } from "react-icons/fa6";
import { GameMode } from "@/lib/types/common/GameMode";
import IconGlowWrapper from "@/app/components/IconGlowWrapper";
import PlayerAvatar, { calculateDuplicateNumbers } from "@/app/components/PlayerAvatar";
import { useIsMobile } from "@/hooks/useIsMobile";
import { AUDIO, useAudio } from "@/app/providers/AudioProvider";

export type ColumnType = 'players' | 'guesses' | 'roundScore' | 'totalScore' | 'streak';


// Column configurations for each game mode
const GAME_MODE_COLUMNS: Partial<Record<GameMode, ColumnType[]>> = {
    [GameMode.TIMED]: ['players', 'guesses', 'roundScore', 'totalScore'],
    [GameMode.CLASSIC]: ['players', 'guesses', 'roundScore', 'totalScore'],
    [GameMode.ENDLESS]: ['players', 'guesses', 'streak'],
    [GameMode.FLASH]: ['players', 'guesses', 'roundScore', 'totalScore'],
    [GameMode.HAZARD]: ['players', 'guesses', 'roundScore', 'totalScore'],
};

interface PlayerResultsListProps {
    roundResult: RoundResult;
    onHoverMapPlayer?: (playerId: string | null) => void;
    onClickMapPlayer?: (playerId: string | null) => void;
    lockedMapPlayer?: string | null;
}

export interface PlayerResultData {
    sessionId: string;
    name: string;
    guessedOnCorrectMap: boolean;
    distance?: number;
    guessedMapName?: string;
    guessedMapId?: string;
    roundScore: number;
    totalScore: number;
    hasGuessed: boolean;
    avatarId: string;
    playerNumber: number;
    currentStreak: number;
    isEliminated: boolean;
    gotPerfectScore: boolean;
    teamId?: string; // Team ID for Fight mode color styling
}

// Calculate distance between two points (simplified for game units)
export const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
};

export default function PlayerResultsList({
    roundResult,
    onHoverMapPlayer,
    onClickMapPlayer,
    lockedMapPlayer,
}: PlayerResultsListProps) {
    const { gameState } = useGame();
    const { allMaps } = useMainMap();
    const { locale } = useTranslation();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { play } = useAudio();
    const columns = useMemo((): ColumnType[] => {
        const baseCols = GAME_MODE_COLUMNS[gameState?.gameMode ?? GameMode.CLASSIC] ?? ['players', 'guesses', 'roundScore', 'totalScore'] as ColumnType[];
        // On mobile, remove totalScore column (no score bar)
        if (isMobile) {
            return baseCols.filter(col => col !== 'totalScore');
        }
        return baseCols;
    }, [gameState?.gameMode, isMobile]);
    const [animatedTotals, setAnimatedTotals] = useState<Record<string, number>>({});
    const [animatedBarWidths, setAnimatedBarWidths] = useState<Record<string, number>>({});
    const [animatedRoundScores, setAnimatedRoundScores] = useState<Record<string, number | undefined>>({});
    const [maxScores, setMaxScores] = useState<Set<string>>(new Set());
    const [flashingBars, setFlashingBars] = useState<Set<string>>(new Set());
    const [animatedStreaks, setAnimatedStreaks] = useState<Record<string, number>>({});
    const MAX_PLAYERS_SINGLE_COLUMN = 6;

    // Process player results
    const playerResults: PlayerResultData[] = Array.from(roundResult.playerResults.entries()).map(([sessionId, player]) => {
        const guess = player.guesses.get(roundResult.roundNumber.toString());
        const roundScore = player.scores[roundResult.roundNumber - 1] || 0;
        const gotPerfectScore = roundScore === 1000;

        if (!guess) {
            return {
                sessionId,
                name: player.name,
                guessedOnCorrectMap: false,
                roundScore: 0,
                totalScore: player.totalScore,
                hasGuessed: false,
                avatarId: player.avatarId,
                playerNumber: player.playerNumber || 0,
                currentStreak: player.currentStreak || 0,
                isEliminated: player.isEliminated || false,
                gotPerfectScore: false,
            };
        }
        const guessedOnCorrectMap = guess.mapId === roundResult.photosphere.mapId;
        let distance: number | undefined;
        let guessedMapName: string | undefined;

        if (guessedOnCorrectMap) {
            // Calculate distance to correct answer
            distance = calculateDistance(
                guess.x,
                guess.y,
                roundResult.photosphere.coord.x,
                roundResult.photosphere.coord.y
            ) - PRECISION_MIN_DISTANCE;
        } else {
            // Find the map they guessed on
            const guessedMap = allMaps.find(map => map.id === guess.mapId);
            guessedMapName = guessedMap ? guessedMap.name[locale as keyof typeof guessedMap.name] : undefined;
        }

        return {
            sessionId,
            name: player.name,
            guessedOnCorrectMap,
            distance,
            guessedMapName,
            guessedMapId: guess.mapId,
            roundScore,
            totalScore: player.totalScore,
            hasGuessed: true,
            avatarId: player.avatarId,
            playerNumber: player.playerNumber || 0,
            currentStreak: player.currentStreak || 0,
            isEliminated: player.isEliminated || false,
            gotPerfectScore,
        };
    });

    // Sort based on which columns are displayed
    if (columns.includes('streak')) {
        // Endless mode: Sort by streak (descending), then by elimination status
        playerResults.sort((a, b) => {
            if (a.isEliminated !== b.isEliminated) {
                return a.isEliminated ? 1 : -1; // Non-eliminated first
            }
            return b.currentStreak - a.currentStreak;
        });
    } else {
        // Classic/Timed mode: Sort by round score (descending)
        playerResults.sort((a, b) => b.roundScore - a.roundScore);
    }

    // Calculate duplicate numbers for avatars
    const duplicateNumbers = useMemo(() => {
        return calculateDuplicateNumbers(playerResults.map(p => ({ sessionId: p.sessionId, avatarId: p.avatarId })));
    }, [playerResults]);

    // Animate total scores and streaks
    useEffect(() => {
        if (columns.includes('totalScore')) {
            setMaxScores(new Set()); // Reset max scores for new round
            setFlashingBars(new Set()); // Reset flashing bars
            playerResults.forEach(player => {
                const prevTotal = player.totalScore - player.roundScore;
                const prevWidth = Math.min((prevTotal / (1000 * roundResult.roundNumber)) * 100, 100);
                const finalWidth = Math.min((player.totalScore / (1000 * roundResult.roundNumber)) * 100, 100);
                setAnimatedTotals(prev => ({ ...prev, [player.sessionId]: prevTotal }));
                setAnimatedBarWidths(prev => ({ ...prev, [player.sessionId]: prevWidth }));
                animate(prevTotal, player.totalScore, {
                    duration: player.roundScore / 500,
                    delay: 1.8,
                    onUpdate: (value) => {
                        setAnimatedTotals(prev => ({ ...prev, [player.sessionId]: Math.round(value) }));
                    }
                });
                animate(prevWidth, finalWidth, {
                    duration: player.roundScore / 500,
                    delay: 1.8,
                    onUpdate: (value) => {
                        setAnimatedBarWidths(prev => ({ ...prev, [player.sessionId]: value }));
                    },
                    onComplete: () => {
                        if (finalWidth === 100) {
                            setMaxScores(prev => new Set(prev).add(player.sessionId));
                            setFlashingBars(prev => new Set(prev).add(player.sessionId));
                            setTimeout(() => {
                                setFlashingBars(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(player.sessionId);
                                    return newSet;
                                });
                            }, 500);
                        }
                    }
                });

                // Animate round scores to 0 and then disappear
                setAnimatedRoundScores(prev => ({ ...prev, [player.sessionId]: player.roundScore }));
                animate(player.roundScore, 0, {
                    duration: player.roundScore / 500,
                    delay: 1.8,
                    onUpdate: (value) => {
                        setAnimatedRoundScores(prev => ({ ...prev, [player.sessionId]: Math.max(0, Math.floor(value)) }));
                    },
                    onComplete: () => {
                        setTimeout(() => {
                            setAnimatedRoundScores(prev => ({ ...prev, [player.sessionId]: undefined }));
                        }, 500);
                    }
                });
            });
        }

        if (columns.includes('streak')) {
            playerResults.forEach(player => {
                const prevStreak = player.currentStreak - (player.gotPerfectScore ? 1 : 0);
                setAnimatedStreaks(prev => ({ ...prev, [player.sessionId]: prevStreak }));

                if (player.gotPerfectScore) {
                    animate(prevStreak, player.currentStreak, {
                        duration: 0.3,
                        delay: 0.7,
                        onUpdate: (value) => {
                            setAnimatedStreaks(prev => ({ ...prev, [player.sessionId]: Math.round(value) }));
                        }
                    });
                }
            });
        }
    }, [roundResult, columns]);



    // Helper function to render player columns
    const renderPlayerColumns = (players: PlayerResultData[], reverse: boolean = false) => {
        const availableComponents: Record<ColumnType, { key: string; element: JSX.Element }> = {
            players: {
                key: 'players',
                element: (
                    <div className="flex flex-col gap-3">
                        {players.map((player) => (
                            <div
                                key={player.sessionId}
                                className={`flex gap-2 items-center ${reverse ? 'justify-end' : ''} ${player.isEliminated ? 'grayscale opacity-50' : ''}`}
                            >
                                {reverse ? (
                                    <>
                                        <div className="flex items-center gap-2">
                                            <div className="text-shadow shadow-blue-800 font-myriad text-sm 2xl:text-xl whitespace-nowrap max-w-[80px] md:max-w-none truncate">{player.name}</div>
                                            <div className="w-6 h-6 flex items-center justify-center">
                                                {gameState?.players?.get(player.sessionId)?.isReady && (
                                                    <Image
                                                        src='/checkmark.png'
                                                        alt="Check Icon"
                                                        height={24}
                                                        width={24}
                                                        className="translate-y-[0px]"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-7 h-7 2xl:w-10 2xl:h-10 relative">
                                            <PlayerAvatar
                                                avatarId={player.avatarId}
                                                duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                size={isMobile ? 28 : 40}
                                                className="w-full h-full"
                                                isLeader={gameState?.gameMasterId === player.sessionId}
                                                leaderIconSize={24}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-7 h-7 2xl:w-10 2xl:h-10 relative">
                                            <PlayerAvatar
                                                avatarId={player.avatarId}
                                                duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                size={isMobile ? 28 : 40}
                                                className="w-full h-full"
                                                isLeader={gameState?.gameMasterId === player.sessionId}
                                                leaderIconSize={24}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-shadow shadow-blue-800 font-myriad text-sm 2xl:text-xl whitespace-nowrap max-w-[80px] md:max-w-none truncate">{player.name}</div>
                                            <div className="w-6 h-6 flex items-center justify-center">
                                                {gameState?.players?.get(player.sessionId)?.isReady && (
                                                    <Image
                                                        src='/checkmark.png'
                                                        alt="Check Icon"
                                                        height={24}
                                                        width={24}
                                                        className="translate-y-[0px]"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )
            },
            guesses: {
                key: 'guesses',
                element: (
                    <div className="flex flex-col gap-3">
                        {players.map((player) => (
                            <div
                                key={player.sessionId}
                                className={`text-shadow shadow-blue-600 font-myriad text-sm 2xl:text-xl whitespace-nowrap flex items-center h-7 2xl:h-10 relative ${player.isEliminated && !player.hasGuessed ? 'opacity-0 pointer-events-none' : ''} ${player.isEliminated && player.hasGuessed ? 'grayscale opacity-50' : ''}`}
                            >
                                {player.hasGuessed && player.guessedOnCorrectMap && (
                                    player.roundScore === 1000 || gameState?.gameMode === GameMode.FLASH ? (
                                        t("play.onTheSpot")
                                    ) : (
                                        <div className="flex gap-3 items-center relative">
                                            {t("play.yalmsAway", { distance: convertDistanceToYalms(player.distance ?? 0) })}
                                            <TooltipWithIconFFXIV
                                                text={t("play.accuracyTooltip")}
                                                size="small"
                                                className="text-sm 2xl:text-lg"
                                            />
                                        </div>
                                    )
                                )}
                                {!player.hasGuessed && t("play.noGuess")}
                                {player.hasGuessed && !player.guessedOnCorrectMap && (
                                    <div className="flex gap-2 items-center">
                                        <span>{player.guessedMapName}</span>
                                        {onHoverMapPlayer && onClickMapPlayer && (
                                            <div
                                                onMouseEnter={() => {
                                                    play(AUDIO.HOVER);
                                                    if (!lockedMapPlayer) {
                                                        onHoverMapPlayer(player.sessionId);
                                                    }
                                                }}
                                                onMouseLeave={() => onHoverMapPlayer(null)}
                                                onClick={() => {
                                                    play(AUDIO.CLICK);
                                                    // If clicking on the same locked player, unlock
                                                    if (lockedMapPlayer === player.sessionId) {
                                                        onClickMapPlayer(null);
                                                    } else {
                                                        // Lock to this player
                                                        onClickMapPlayer(player.sessionId);
                                                    }
                                                }}
                                                className={`cursor-pointer flex items-center transition-all w-[20px] h-[20px] 2xl:w-[35px] 2xl:h-[35px] duration-200 ${lockedMapPlayer === player.sessionId
                                                    ? 'scale-110 drop-shadow-[0_0_8px_rgba(37,99,235,0.8)]'
                                                    : 'hover:scale-105'
                                                    }`}
                                            >
                                                <Image
                                                    src="/map_icon.png"
                                                    alt="View Map"
                                                    width={35}
                                                    height={35}
                                                    className={`${lockedMapPlayer === player.sessionId
                                                        ? 'brightness-125 saturate-150'
                                                        : ''
                                                        }`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )
            },
            roundScore: {
                key: 'roundScore',
                element: (
                    <div className="flex flex-col gap-3">
                        {players.map((player) => (
                            <div
                                key={player.sessionId}
                                className="text-shadow shadow-blue-800 font-contrax text-sm 2xl:text-lg  whitespace-nowrap flex items-center h-7 2xl:h-10"
                            >
                                +{player.roundScore}
                            </div>
                        ))}
                    </div>
                )
            },
            totalScore: {
                key: 'totalScore',
                element: (
                    <div className="flex flex-col gap-3">
                        {players.map((player) => (
                            <div
                                key={player.sessionId}
                                className="text-black font-contrax text-xl whitespace-nowrap flex items-center justify-center h-7 2xl:h-10 w-[88px]"
                            >
                                <div className={`w-full min-w-[100px] h-2 rounded-sm border ${maxScores.has(player.sessionId) ? 'border-yellow-500 shadow-[0_0px_10px_#eab308]' : 'border-blue-400 shadow-[0_0px_10px_#2563eb]'} relative`}>
                                    <AnimatePresence>
                                        {animatedRoundScores[player.sessionId] !== undefined && (
                                            <motion.span
                                                key={player.sessionId}
                                                initial={{ opacity: 0, y: 5, x: "-50%", scale: 0.8 }}
                                                animate={{ opacity: 1, y: 0, x: "-50%", scale: 1 }}
                                                exit={{ opacity: 0, transition: { delay: 0.3 } }}
                                                transition={{ duration: 0.1, ease: "easeOut", delay: 1 }}
                                                className="absolute top-[-17px] left-1/2 text-xs text-yellow-200 text-shadow-border shadow-amber-800/50"
                                            >
                                                +{animatedRoundScores[player.sessionId]}
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                    <motion.div
                                        className={`absolute left-0 top-0 h-[6px] rounded-[1px] origin-center bg-white`}
                                        style={{ width: `${animatedBarWidths[player.sessionId] ?? 0}%` }}
                                        animate={flashingBars.has(player.sessionId) ? {
                                            boxShadow: ['0 0 0px rgba(255,255,255,0)', '0 0 20px rgba(255,255,255,1)', '0 0 0px rgba(255,255,255,0)']
                                        } : {}}
                                        transition={{ duration: 0.1 }}
                                    />
                                    <span className={`${maxScores.has(player.sessionId) ? 'text-yellow-200 text-shadow shadow-amber-900' : 'text-white text-shadow shadow-blue-900'} text-sm text-right absolute bottom-[-14px] right-0`}>{animatedTotals[player.sessionId] ?? player.totalScore}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            },
            streak: {
                key: 'streak',
                element: (
                    <div className="flex flex-col gap-3">
                        {players.map((player) => (
                            <div
                                key={player.sessionId}
                                className={`text-base 2xl:text-2xl whitespace-nowrap flex items-center justify-center h-7 2xl:h-10 gap-2 ${player.isEliminated ? 'grayscale opacity-50' : ''}`}
                            >
                                <IconGlowWrapper icon={<FaFireFlameCurved className="text-sm 2xl:text-xl" />} color="blue" />
                                <span className="text-shadow shadow-blue-800">{t("play.streak")}</span>
                                <motion.span
                                    animate={player.gotPerfectScore && !player.isEliminated ? {
                                        scale: [1, 1.5, 1],
                                        color: [
                                            'rgb(255, 255, 255)',
                                            'rgb(250, 204, 21)',
                                            'rgb(255, 255, 255)'
                                        ],
                                        textShadow: [
                                            '0 0 5px rgba(255, 255, 255, 0.8)',
                                            '0 0 20px rgba(250, 204, 21, 1)',
                                            '0 0 5px rgba(255, 255, 255, 0.8)'
                                        ]
                                    } : {}}
                                    transition={{ duration: 0.3, delay: 0.7, ease: "easeOut" }}
                                >
                                    {animatedStreaks[player.sessionId] ?? player.currentStreak}
                                </motion.span>
                            </div>
                        ))}
                    </div>
                )
            },
        };

        const selectedComponents = columns.map((col: ColumnType) => availableComponents[col]);
        const orderedComponents = reverse ? [...selectedComponents].reverse() : selectedComponents;

        return (
            <div className={`flex ${isMobile ? 'gap-2' : 'gap-8'} items-center`}>
                {orderedComponents.map((comp: { key: string; element: JSX.Element }) => (
                    <div key={comp.key}>
                        {comp.element}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="relative">
            <div className="absolute w-full translate-x-[-25px] h-full flex z-[1] blur-sm">
                <div className="w-1/2 h-full bg-gradient-to-l from-black/50 via-black/30 via-[88%] to-transparent"></div>
                <div className="w-1/2 h-full bg-gradient-to-r from-black/50 via-black/30 via-[88%] to-transparent"></div>
            </div>
            <div className="relative w-full flex items-center justify-center text-white md:max-h-[250px] 2xl:max-h-[300px] md:overflow-y-auto 2xl:overflow-y-visible px-2 z-[2]">
                {/* On mobile, always single column */}
                {isMobile || playerResults.length <= MAX_PLAYERS_SINGLE_COLUMN ? (
                    <div className="mt-4">
                        {renderPlayerColumns(playerResults)}
                    </div>
                ) : (() => {
                    const mid = Math.ceil(playerResults.length / 2);
                    const firstColumn = playerResults.slice(0, mid);
                    const secondColumn = playerResults.slice(mid);
                    return (
                        <div className="flex gap-10 items-start mt-4">
                            {renderPlayerColumns(firstColumn)}
                            <div className="w-[2px] bg-white self-stretch"></div>
                            {renderPlayerColumns(secondColumn, true)}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
}